import React from 'react';
import { LinkProps } from 'react-router-dom';

import { Mode } from '@common/react/objects/Core';
import LinkWithPrevLocation from '@common/react/components/UI/LinkWithPrevLocation/LinkWithPrevLocation';

import { PageType } from '@commonTuna/react/objects/Page';

import {
	AnalysisTabs,
	AppointmentTabs,
	BaseLinkBuilder,
	DoctorTabs,
	EditorLinkBuilder,
	EditorWithTabsLinkBuilder,
	LinkBuilder,
	LocationTabs,
	PatientNoteTabs,
	PatientTabs,
	PatientTabsGroup,
	PatientTabsGroupKeys,
	PatientTabsKeys,
	ProcedureTabs,
	ProductTabs,
	useLinkBuilder,
	UserTabs,
	PageTabs,
	InquiryTabs,
} from '@app/hooks/useLinkBuilder';

export {
	UserTabs,
	LocationTabs,
	DoctorTabs,
	PatientTabs,
	AppointmentTabs,
	PatientNoteTabs,
	ProcedureTabs,
	ProductTabs,
} from '@app/hooks/useLinkBuilder';

export const getDefaultPatientTab = (withGroups: boolean) => (withGroups
	? PatientTabsGroupKeys[PatientTabsGroup.General]
	: PatientTabsKeys[PatientTabs.General]);

interface LinkWithBuilderProps extends Omit<LinkProps, 'to'> {
	build: (builder: LinkBuilder) => BaseLinkBuilder;
}

const LinkWithBuilder: React.FC<LinkWithBuilderProps> = ({
	build,
	...props
}) => {
	const linkBuilder = useLinkBuilder();

	return <LinkWithPrevLocation
		{...props}
		to={build(linkBuilder).link()}
	/>;
};

interface LinkWithId extends Omit<LinkProps, 'to' | 'id'> {
	id: number | null | undefined;
}

interface LinkWithIdBuilderProps<BuilderType extends BaseLinkBuilder> extends Omit<LinkWithId, 'build'> {
	build: (builder: LinkBuilder, id: number) => BuilderType;
}

const LinkWithId: <BuilderType extends BaseLinkBuilder>
(p: LinkWithIdBuilderProps<BuilderType>) => React.ReactElement = <BuilderType extends BaseLinkBuilder, >({
	id,
	build,
	...props
}: LinkWithIdBuilderProps<BuilderType>) => {
	if (!id) {
		if (props.className) {
			return <a className={props.className}>
				{props.children}
			</a>;
		}
		return <>{props.children}</>;
	}

	return <LinkWithBuilder
		{...props}
		build={(builder) => build(builder, id)}
	/>;
};

interface LinkWithEdit extends LinkWithId {
	edit?: boolean;
}

interface LinkWithEditBuilderProps<BuilderType extends EditorLinkBuilder> extends Omit<LinkWithId, 'build'> {
	build: (builder: LinkBuilder, id: number) => BuilderType;
	edit?: boolean;
}

const LinkWithEdit: <BuilderType extends EditorLinkBuilder>
(p: LinkWithEditBuilderProps<BuilderType>) => React.ReactElement = <BuilderType extends EditorLinkBuilder, >({
	build,
	edit,
	...props
}: LinkWithEditBuilderProps<BuilderType>) => {
	return <LinkWithId<BuilderType>
		{...props}
		build={(builder, id) => (edit
			? build(builder, id).mode(Mode.Edit)
			: build(builder, id)) as BuilderType}
	/>;
};

interface LinkWithTabs<T> extends LinkWithEdit {
	tab?: T;
}

interface LinkWithTabsBuilderProps<TabType, BuilderType> extends Omit<LinkWithTabs<TabType>, 'build'> {
	build: (builder: LinkBuilder, id: number) => EditorWithTabsLinkBuilder<TabType, BuilderType>;
}

const LinkWithTabs: <TabType, BuilderType extends EditorLinkBuilder>
(p: LinkWithTabsBuilderProps<TabType, BuilderType>) => React.ReactElement<TabType> = <TabType, BuilderType extends EditorLinkBuilder, >({
	build,
	tab,
	...props
}: LinkWithTabsBuilderProps<TabType, BuilderType>) => {
	return <LinkWithEdit
		{...props}
		build={(builder, id) => build(builder, id).tab(tab)}
	/>;
};

interface PatientLinkProps extends LinkWithTabs<PatientTabs> {
	noteTab?: PatientNoteTabs;
}

const PatientLink: React.FC<PatientLinkProps> = ({
	id,
	tab,
	edit,
	noteTab,
	...props
}) => {
	if (!id) {
		return <>{props.children}</>;
	}

	return <LinkWithBuilder
		{...props}
		build={(builder) => (edit
			? builder.patient(id).tab(tab).notes(noteTab).mode(Mode.Edit)
			: builder.patient(id).tab(tab).notes(noteTab))}
	/>;
};

interface AppointmentLinkProps extends LinkWithTabs<AppointmentTabs> {
	appointmentId: number | null | undefined;
	noteTab?: PatientNoteTabs;
}

const AppointmentLink: React.FC<AppointmentLinkProps> = ({
	id,
	appointmentId,
	tab,
	noteTab,
	edit,
	...props
}) => {
	if (!id || !appointmentId) {
		return <>{props.children}</>;
	}

	return <LinkWithBuilder
		{...props}
		build={(builder) => (edit
			? builder.appointment(id, appointmentId).tab(tab).notes(noteTab).mode(Mode.Edit)
			: builder.appointment(id, appointmentId).tab(tab).notes(noteTab))}
	/>;
};

const UserLink: React.FC<LinkWithTabs<UserTabs>> = (props) =>
	<LinkWithTabs<UserTabs, EditorLinkBuilder> {...props} build={(builder, id) => builder.user(id)} />;

const LocationLink: React.FC<LinkWithTabs<LocationTabs>> = (props) =>
	<LinkWithTabs<LocationTabs, EditorLinkBuilder> {...props} build={(builder, id) => builder.location(id)} />;

const DoctorLink: React.FC<LinkWithTabs<DoctorTabs>> = (props) =>
	<LinkWithTabs<DoctorTabs, EditorLinkBuilder> {...props} build={(builder, id) => builder.doctor(id)} />;

const InsuranceLink: React.FC<LinkWithEdit> = (props) =>
	<LinkWithId {...props} build={(builder, id) => builder.insurance(id)} />;

const InvoiceLink: React.FC<LinkWithEdit> = (props) =>
	<LinkWithId {...props} build={(builder, id) => builder.invoice(id)} />;

const ProductLink: React.FC<LinkWithTabs<ProductTabs>> = (props) =>
	<LinkWithId {...props} build={(builder, id) => builder.product(id)} />;

const ProcedureLink: React.FC<LinkWithTabs<ProcedureTabs>> = (props) =>
	<LinkWithId {...props} build={(builder, id) => builder.procedure(id)} />;

const SpecialLink: React.FC<LinkWithEdit> = (props) =>
	<LinkWithId {...props} build={(builder, id) => builder.special(id)} />;

const SaleLink: React.FC<LinkWithEdit> = (props) =>
	<LinkWithId {...props} build={(builder, id) => builder.sale(id)} />;

const InquiryLink: React.FC<LinkWithTabs<InquiryTabs>> = (props) =>
	<LinkWithTabs<InquiryTabs, EditorLinkBuilder> {...props} build={(builder, id) => builder.inquiry(id)} />;

const ReviewLink: React.FC<LinkWithEdit> = (props) =>
	<LinkWithId {...props} build={(builder, id) => builder.review(id)} />;

const CaseLink: React.FC<LinkWithEdit> = (props) =>
	<LinkWithId {...props} build={(builder, id) => builder.case(id)} />;

const WizardLink: React.FC<LinkWithId> = (props) =>
	<LinkWithId {...props} build={(builder, id) => builder.wizard(id)} />;

const CheckoutLink: React.FC<LinkWithId> = (props) =>
	<LinkWithId {...props} build={(builder, id) => builder.checkout(id)} />;

interface PageLinkProps extends LinkWithTabs<PageTabs> {
	pageType?: PageType;
}

const PageLink: React.FC<PageLinkProps> = ({
	id,
	tab,
	pageType = PageType.SERVICE,
	...props
}) => {
	if (!id) {
		return <a className={props.className} title={props.title}>{props.children}</a>;
	}

	return <LinkWithBuilder
		{...props}
		build={(builder) => builder.page(id, pageType)
			.tab(tab)}
	/>;
};

interface AnalysisLinkProps extends LinkWithTabs<AnalysisTabs> {
	patientId?: number | null | undefined;
	appointmentId?: number | null | undefined;
}

const AnalysisLink: React.FC<AnalysisLinkProps> = ({
	id,
	tab,
	patientId,
	appointmentId,
	...props
}) => {
	if (!id) {
		return <>{props.children}</>;
	}

	return <LinkWithBuilder
		{...props}
		build={(builder) => builder.analysis(id)
			.tab(tab)
			.addIfHasValue('patientId', `${patientId ?? ''}`)
			.addIfHasValue('appointmentId', `${appointmentId ?? ''}`)}
	/>;
};

interface CalendarLinkProps extends Omit<LinkProps, 'to'> {
	locationId: number | null | undefined;
	appointmentId: number | null | undefined;
	date: number | null | undefined;
}

const CalendarLink: React.FC<CalendarLinkProps> = ({
	locationId,
	appointmentId,
	date,
	...props
}) => {
	return <LinkWithBuilder
		{...props}
		build={(builder) => builder.calendar().locationId(locationId).appointmentId(appointmentId).date(date)}
	/>;
};

type LinksExtensions = {
	Analysis: typeof AnalysisLink;
	Appointment: typeof AppointmentLink;
	Patient: typeof PatientLink;
	User: typeof UserLink;
	Location: typeof LocationLink;
	Doctor: typeof DoctorLink;
	Insurance: typeof InsuranceLink;
	Invoice: typeof InvoiceLink;
	Product: typeof ProductLink;
	Procedure: typeof ProcedureLink;
	Special: typeof SpecialLink;
	Sale: typeof SaleLink;
	Inquiry: typeof InquiryLink;
	Review: typeof ReviewLink;
	Case: typeof CaseLink;
	Wizard: typeof WizardLink;
	Checkout: typeof CheckoutLink;
	Calendar: typeof CalendarLink;
	Page: typeof PageLink;
};

export const Links: React.FC & LinksExtensions = ({ children }) => (<>{children}</>);

Links.Analysis = AnalysisLink;
Links.Appointment = AppointmentLink;
Links.Patient = PatientLink;
Links.User = UserLink;
Links.Location = LocationLink;
Links.Doctor = DoctorLink;
Links.Insurance = InsuranceLink;
Links.Invoice = InvoiceLink;
Links.Product = ProductLink;
Links.Procedure = ProcedureLink;
Links.Special = SpecialLink;
Links.Sale = SaleLink;
Links.Inquiry = InquiryLink;
Links.Review = ReviewLink;
Links.Case = CaseLink;
Links.Wizard = WizardLink;
Links.Checkout = CheckoutLink;
Links.Calendar = CalendarLink;
Links.Page = PageLink;
